import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { Badge, Button, Input, MetricRow, Modal, Spinner } from '@/components/ui';
import {
  exemptAssignment,
  exportAcknowledgements,
  getPolicyReport,
  listOutstanding,
  type OutstandingRow,
  type PolicyContext,
} from '@/api/services/hrPolicies.service';
import { useCompanyNavList } from '@/features/companies/hooks/useCompanyNavList';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { completionPercent, formatPolicyDate } from './policyFormat';

const PAGE = 25;

function ExemptDialog({
  context,
  row,
  onClose,
  onDone,
}: {
  context: PolicyContext;
  row: OutstandingRow;
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const exempt = useMutation({
    mutationFn: () => exemptAssignment(context, row.assignmentId, reason.trim()),
    onSuccess: () => {
      toast.success(`${row.name || 'Employee'} exempted`);
      onDone();
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  return (
    <Modal open onClose={onClose} title={`Exempt ${row.name || 'employee'}`} className="max-w-md">
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (reason.trim().length >= 3) exempt.mutate();
        }}
      >
        <Input
          id="exempt-reason"
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <p className="text-xs text-gray-600 dark:text-gray-300">
          They will no longer be asked to acknowledge this version. The reason is recorded.
        </p>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={reason.trim().length < 3}
            loading={exempt.isPending}
          >
            Exempt
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Completion for one published version: current-audience figures, a company
 * and department breakdown, the outstanding list, and a CSV export.
 */
export function PolicyReportPanel({
  context,
  versionId,
  canExempt,
}: {
  context: PolicyContext;
  versionId: string;
  canExempt: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [exempting, setExempting] = useState<OutstandingRow | null>(null);
  const contextKey = context.companyId ?? 'organization';
  const { data: companyData } = useCompanyNavList();
  const companies = Array.isArray(companyData) ? companyData : [];
  const companyName = (id: string) =>
    companies.find((company) => company._id === id)?.name ?? 'Company';

  const report = useQuery({
    queryKey: ['hr-policy-report', contextKey, versionId],
    queryFn: () => getPolicyReport(context, versionId),
  });
  const outstanding = useQuery({
    queryKey: ['hr-policy-outstanding', contextKey, versionId, page, overdueOnly],
    queryFn: () => listOutstanding(context, versionId, { page, limit: PAGE, overdueOnly }),
  });
  const download = useMutation({
    mutationFn: () => exportAcknowledgements(context, versionId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `policy-acknowledgements-v${report.data?.version ?? ''}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  if (report.isLoading) {
    return (
      <div className="flex justify-center py-10" role="status">
        <Spinner />
        <span className="sr-only">Loading report</span>
      </div>
    );
  }
  if (report.error || !report.data) {
    return (
      <p className="text-sm text-danger" role="alert">
        {getErrorMessage(report.error)}
      </p>
    );
  }

  const { summary, breakdown, mode } = report.data;
  const complete = summary.acknowledged + summary.carriedForward;
  const rows = Array.isArray(outstanding.data?.data) ? outstanding.data.data : [];
  const total = outstanding.data?.total ?? 0;

  if (mode === 'none') {
    return (
      <p className="text-sm text-gray-700 dark:text-gray-200">
        This version is for reading only. {summary.audience} employees can read it.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        <MetricRow
          label="Acknowledged"
          value={`${completionPercent(complete, summary.audience)}%`}
          hint={`${complete} of ${summary.audience} in the audience`}
          tone={complete === summary.audience && summary.audience > 0 ? 'positive' : 'neutral'}
        />
        <MetricRow label="Still to acknowledge" value={summary.pending} tone="pending" />
        <MetricRow
          label="Past their due date"
          value={summary.overdue}
          tone={summary.overdue ? 'critical' : 'neutral'}
        />
        <MetricRow label="Exempted" value={summary.exempted} />
      </div>
      {(summary.carriedForward > 0 || summary.acknowledgedThenLeft > 0 || summary.removed > 0) && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          {summary.carriedForward > 0 &&
            `${summary.carriedForward} covered by an earlier version. `}
          {summary.acknowledgedThenLeft > 0 &&
            `${summary.acknowledgedThenLeft} acknowledged and have since left the audience. `}
          {summary.removed > 0 && `${summary.removed} left before acknowledging.`}
        </p>
      )}

      {breakdown.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-left text-sm">
            <caption className="sr-only">Completion by company and department</caption>
            <thead className="text-xs text-gray-600 dark:text-gray-300">
              <tr>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Company
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Department
                </th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">
                  Complete
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  Waiting
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {breakdown.map((row) => (
                <tr key={`${row.companyId}:${row.departmentId ?? ''}`}>
                  <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">
                    {companyName(row.companyId)}
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-200">
                    {row.departmentName ?? 'No department'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {row.complete}/{row.audience}
                  </td>
                  <td className="py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {row.pending}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section aria-labelledby="outstanding-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3
            id="outstanding-heading"
            className="text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            Waiting for acknowledgement
          </h3>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={overdueOnly}
                onChange={(event) => {
                  setOverdueOnly(event.target.checked);
                  setPage(1);
                }}
              />
              Overdue only
            </label>
            <Button
              variant="secondary"
              size="sm"
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              <Download className="mr-1 h-4 w-4" aria-hidden />
              Export CSV
            </Button>
          </div>
        </div>
        {outstanding.isLoading ? (
          <div className="flex justify-center py-6" role="status">
            <Spinner />
            <span className="sr-only">Loading outstanding acknowledgements</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {overdueOnly ? 'Nobody is overdue.' : 'Nobody is waiting. Everyone is up to date.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {rows.map((row) => (
              <li
                key={row.assignmentId}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.name || row.email || 'Employee'}
                    {row.employeeId && (
                      <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                        {row.employeeId}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Due {formatPolicyDate(row.dueAt, context.timeZone)}
                    {row.viewed ? ' · opened' : ' · not opened'}
                    {row.remindersSent > 0 && ` · ${row.remindersSent} reminder${row.remindersSent > 1 ? 's' : ''}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {row.overdue && <Badge variant="warning">Overdue</Badge>}
                  {canExempt && (
                    <Button variant="ghost" size="sm" onClick={() => setExempting(row)}>
                      Exempt <span className="sr-only">{row.name}</span>
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {total > PAGE && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-300">
              Page {page} of {Math.ceil(total / PAGE)}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page * PAGE >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </section>
      {exempting && (
        <ExemptDialog
          context={context}
          row={exempting}
          onClose={() => setExempting(null)}
          onDone={() => {
            setExempting(null);
            void queryClient.invalidateQueries({ queryKey: ['hr-policy-report', contextKey] });
            void queryClient.invalidateQueries({ queryKey: ['hr-policy-outstanding', contextKey] });
          }}
        />
      )}
    </div>
  );
}
